import { RECORDING_TIMESLICE_MS, formatTimestamp } from '@sl/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { meetingsApi } from '../lib/api.js';
import { ChunkQueue, type ChunkQueueState } from '../lib/chunkQueue.js';
import { pendingUpload } from '../lib/chunkStore.js';
import { clientLog } from '../lib/clientLog.js';
import { SilenceDetector, computeRms, rmsToLevel } from '../lib/micLevel.js';

type Phase = 'idle' | 'recording' | 'finishing' | 'stopFailed' | 'done';

/** Whether the live meter has an active AudioContext feeding it. */
type MicCheckState = 'idle' | 'starting' | 'needsGesture' | 'active' | 'unavailable';

interface WakeLockSentinelLike {
  release: () => Promise<void>;
}

interface WindowWithWebkitAudioContext extends Window {
  webkitAudioContext?: typeof AudioContext;
}

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
];

/** No chunk within two timeslices means the capture has died, not that it is quiet. */
const WATCHDOG_MS = RECORDING_TIMESLICE_MS * 2;

/** How often the meter samples the analyser, throttled off the rAF loop. */
const METER_SAMPLE_MS = 100;

const MIC_DEVICE_STORAGE_KEY = 'sl-meetings-mic-device';

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
}

function readStoredMicId(): string | undefined {
  try {
    return localStorage.getItem(MIC_DEVICE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function storeMicId(deviceId: string): void {
  try {
    localStorage.setItem(MIC_DEVICE_STORAGE_KEY, deviceId);
  } catch {
    /* private mode */
  }
}

function micConstraints(deviceId?: string): MediaTrackConstraints {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    channelCount: 1,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  };
}

/** Falls back to the default mic if the stored/chosen device is now invalid. */
async function openMic(deviceId?: string): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: micConstraints(deviceId) });
  } catch (err) {
    if (!deviceId) throw err;
    return navigator.mediaDevices.getUserMedia({ audio: micConstraints(undefined) });
  }
}

const EMPTY_QUEUE_STATE: ChunkQueueState = {
  uploaded: 0,
  pending: 0,
  failed: 0,
  stalled: false,
  online: true,
  lastError: null,
};

export function RecorderPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [captureLost, setCaptureLost] = useState(false);
  const [wakeLockHeld, setWakeLockHeld] = useState(false);
  const [resumable, setResumable] = useState<number | null>(null);
  const [queueState, setQueueState] = useState<ChunkQueueState>(EMPTY_QUEUE_STATE);

  const [micLevel, setMicLevel] = useState(0);
  const [micLabel, setMicLabel] = useState<string | null>(null);
  const [micCheckState, setMicCheckState] = useState<MicCheckState>('idle');
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string | undefined>(readStoredMicId);
  const [silenceWarning, setSilenceWarning] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const queueRef = useRef<ChunkQueue | null>(null);
  const chunkIndexRef = useRef(0);
  const lastDataAtRef = useRef(0);
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);
  const timerRef = useRef<number | null>(null);
  const watchdogRef = useRef<number | null>(null);
  /** One diagnostic line per watchdog trip, not one every half-timeslice. */
  const watchdogTrippedRef = useRef(false);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const meterRafRef = useRef<number | null>(null);
  const meterBufferRef = useRef<Float32Array<ArrayBuffer> | null>(null);
  const lastMeterSampleAtRef = useRef(0);
  const silenceDetectorRef = useRef(new SilenceDetector());
  /** Mirrors `phase` so the rAF meter loop always reads the latest value. */
  const phaseRef = useRef<Phase>('idle');

  useEffect(() => {
    phaseRef.current = phase;
    if (phase !== 'recording') {
      setSilenceWarning(false);
      silenceDetectorRef.current.reset();
    }
  }, [phase]);

  const { data: meetingData } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id),
    enabled: Boolean(id),
  });

  // Offer to finish an upload a previous session left behind.
  useEffect(() => {
    let cancelled = false;
    if (!id) return undefined;
    void pendingUpload(id).then((pending) => {
      if (!cancelled && pending && pending.chunks.length > 0) {
        setResumable(pending.chunks.length);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const releaseWakeLock = useCallback(async (): Promise<void> => {
    try {
      await wakeLockRef.current?.release();
    } catch {
      /* already released */
    }
    wakeLockRef.current = null;
    setWakeLockHeld(false);
  }, []);

  const requestWakeLock = useCallback(async (): Promise<void> => {
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
    };
    if (!nav.wakeLock) return;
    try {
      wakeLockRef.current = await nav.wakeLock.request('screen');
      setWakeLockHeld(true);
    } catch {
      setWakeLockHeld(false);
    }
  }, []);

  const clearWatchdog = useCallback((): void => {
    if (watchdogRef.current) window.clearInterval(watchdogRef.current);
    watchdogRef.current = null;
  }, []);

  const stopMeterLoop = useCallback((): void => {
    if (meterRafRef.current !== null) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
  }, []);

  /** Closes the monitoring-only AudioContext; never touches the mic tracks. */
  const closeAudioContext = useCallback(async (): Promise<void> => {
    stopMeterLoop();
    analyserRef.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx && ctx.state !== 'closed') {
      try {
        await ctx.close();
      } catch {
        /* already closed */
      }
    }
  }, [stopMeterLoop]);

  const sampleMeter = useCallback((): void => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const now = performance.now();
    if (now - lastMeterSampleAtRef.current >= METER_SAMPLE_MS) {
      lastMeterSampleAtRef.current = now;
      if (!meterBufferRef.current || meterBufferRef.current.length !== analyser.fftSize) {
        meterBufferRef.current = new Float32Array(analyser.fftSize);
      }
      analyser.getFloatTimeDomainData(meterBufferRef.current);
      const level = rmsToLevel(computeRms(meterBufferRef.current));
      setMicLevel(level);
      if (phaseRef.current === 'recording') {
        const event = silenceDetectorRef.current.update(level, Date.now());
        if (event === 'warning') {
          setSilenceWarning(true);
          clientLog('recorder.silence.warning', {}, { level: 'warn', meetingId: id });
        } else if (event === 'cleared') {
          setSilenceWarning(false);
          clientLog('recorder.silence.cleared', {}, { level: 'info', meetingId: id });
        }
      }
    }
    meterRafRef.current = requestAnimationFrame(sampleMeter);
  }, [id]);

  const startMeterLoop = useCallback((): void => {
    stopMeterLoop();
    lastMeterSampleAtRef.current = 0;
    meterRafRef.current = requestAnimationFrame(sampleMeter);
  }, [sampleMeter, stopMeterLoop]);

  /**
   * Wires an AnalyserNode onto `stream` for monitoring only (never connected
   * to the destination, so nothing is played back). On iOS the AudioContext
   * can only be created/resumed from inside a user gesture; when that is not
   * where we are called from and the browser leaves it suspended, we back
   * off and let the UI offer an explicit "Start mic check" button instead.
   */
  const attachMeter = useCallback(
    async (stream: MediaStream, fromGesture: boolean): Promise<void> => {
      const AudioContextCtor =
        window.AudioContext ?? (window as WindowWithWebkitAudioContext).webkitAudioContext;
      if (!AudioContextCtor) {
        setMicCheckState('unavailable');
        return;
      }
      await closeAudioContext();
      const ctx = new AudioContextCtor();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser); // monitoring only: never connected to ctx.destination
      analyserRef.current = analyser;

      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch {
          /* iOS autoplay policy: needs a user gesture */
        }
      }
      if (ctx.state === 'suspended' && !fromGesture) {
        setMicCheckState('needsGesture');
        clientLog('recorder.preflight.gestureRequired', {}, { level: 'warn', meetingId: id });
        return;
      }
      setMicCheckState('active');
      startMeterLoop();
    },
    [closeAudioContext, id, startMeterLoop],
  );

  /** Requests the mic and starts the "Mic check" meter before Record is pressed. */
  const runPreflight = useCallback(
    async (fromGesture: boolean, deviceIdOverride?: string): Promise<void> => {
      if (phaseRef.current !== 'idle') return;
      setMicCheckState('starting');
      try {
        const stream = await openMic(deviceIdOverride ?? selectedMicId);
        streamRef.current = stream;
        const [track] = stream.getAudioTracks();
        setMicLabel(track?.label || null);
        await attachMeter(stream, fromGesture);
        if (navigator.mediaDevices?.enumerateDevices) {
          const list = await navigator.mediaDevices.enumerateDevices();
          setMicDevices(list.filter((d) => d.kind === 'audioinput'));
        }
      } catch (err) {
        setMicCheckState('unavailable');
        clientLog(
          'recorder.preflight.failed',
          { errorName: err instanceof Error ? err.name : 'unknown' },
          { level: 'warn', meetingId: id },
        );
      }
    },
    [attachMeter, id, selectedMicId],
  );

  // Best-effort mic check as soon as the recorder opens. On iOS this often
  // cannot resume the AudioContext without a gesture; attachMeter notices
  // and falls back to `needsGesture`, handled by the "Start mic check" button.
  useEffect(() => {
    void runPreflight(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartMicCheck = useCallback((): void => {
    const stream = streamRef.current;
    if (stream && stream.getAudioTracks().some((track) => track.readyState === 'live')) {
      void attachMeter(stream, true);
    } else {
      void runPreflight(true);
    }
  }, [attachMeter, runPreflight]);

  const handleMicChange = useCallback(
    async (deviceId: string): Promise<void> => {
      setSelectedMicId(deviceId);
      storeMicId(deviceId);
      if (phaseRef.current !== 'idle') return;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      await closeAudioContext();
      // The <select> onChange is itself a user gesture.
      await runPreflight(true, deviceId);
    },
    [closeAudioContext, runPreflight],
  );

  // Re-acquire the wake lock when the tab comes back to the foreground.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && phase === 'recording') void requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [phase, requestWakeLock]);

  // Warn before leaving while a recording is live or still uploading.
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent): void => {
      if (phase === 'recording' || phase === 'finishing' || queueState.pending > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [phase, queueState.pending]);

  useEffect(
    () => () => {
      queueRef.current?.dispose();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (watchdogRef.current) window.clearInterval(watchdogRef.current);
      void closeAudioContext();
      void releaseWakeLock();
    },
    [closeAudioContext, releaseWakeLock],
  );

  const start = async (restart = false): Promise<void> => {
    setError(null);
    setCaptureLost(false);
    setSilenceWarning(false);
    silenceDetectorRef.current.reset();
    const mimeType = pickMimeType();
    if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
      setError(t('recorder.unsupported'));
      return;
    }

    // Reuse the "Mic check" stream when it is still live: same constraints,
    // no second permission round-trip, and the meter keeps running unbroken.
    const existing = streamRef.current;
    let stream: MediaStream;
    if (existing && existing.getAudioTracks().some((track) => track.readyState === 'live')) {
      stream = existing;
    } else {
      try {
        stream = await openMic(selectedMicId);
      } catch {
        setError(t('recorder.micDenied'));
        return;
      }
    }

    // The Start-recording click is itself a user gesture: guarantees the
    // AudioContext can be created/resumed even if the pre-flight mic check
    // never got past iOS's autoplay gate.
    if (!audioCtxRef.current || audioCtxRef.current.state !== 'running') {
      await attachMeter(stream, true);
    }

    let attempt = 0;
    try {
      const started = await meetingsApi.startRecording(id, restart);
      attempt = started.attempt ?? 0;
    } catch (err) {
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      void closeAudioContext();
      setMicCheckState('idle');
      setError(err instanceof Error ? err.message : t('common.error'));
      return;
    }

    streamRef.current = stream;
    chunkIndexRef.current = 0;
    queueRef.current = new ChunkQueue(id, attempt, mimeType, setQueueState);
    setResumable(null);

    // A track that ends (device unplugged, OS grab) stops producing audio
    // silently; surface it instead of recording nothing.
    for (const track of stream.getAudioTracks()) {
      track.addEventListener('ended', () => {
        clientLog('recorder.track.ended', { label: track.label }, { level: 'error', meetingId: id });
        setCaptureLost(true);
      });
      track.addEventListener('mute', () => {
        clientLog('recorder.track.mute', { label: track.label }, { level: 'warn', meetingId: id });
        setCaptureLost(true);
      });
      track.addEventListener('unmute', () => {
        clientLog('recorder.track.unmute', { label: track.label }, { level: 'info', meetingId: id });
        setCaptureLost(false);
      });
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64_000 });
    } catch (err) {
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      void closeAudioContext();
      setMicCheckState('idle');
      setError(err instanceof Error ? err.message : t('recorder.unsupported'));
      return;
    }

    recorder.ondataavailable = (event) => {
      const now = Date.now();
      const sincePrevious = now - lastDataAtRef.current;
      lastDataAtRef.current = now;
      watchdogTrippedRef.current = false;
      if (event.data && event.data.size > 0) {
        clientLog(
          'recorder.dataavailable',
          { index: chunkIndexRef.current, size: event.data.size, msSincePrevious: sincePrevious },
          { level: 'info', meetingId: id },
        );
        queueRef.current?.push(chunkIndexRef.current, event.data);
        chunkIndexRef.current += 1;
        setCaptureLost(false);
      }
    };
    recorder.onerror = (event) => {
      const detail = (event as Event & { error?: DOMException }).error;
      clientLog(
        'recorder.error',
        { errorName: detail?.name ?? 'unknown', errorMessage: detail?.message ?? '' },
        { level: 'error', meetingId: id },
      );
      setError(detail?.message ?? t('recorder.recorderError'));
      setCaptureLost(true);
    };

    recorder.start(RECORDING_TIMESLICE_MS);
    const [primaryTrack] = stream.getAudioTracks();
    clientLog(
      'recorder.start',
      {
        mimeType,
        timesliceMs: RECORDING_TIMESLICE_MS,
        restart,
        micLabel: primaryTrack?.label || null,
        micDeviceId: primaryTrack?.getSettings().deviceId ?? null,
      },
      { level: 'info', meetingId: id },
    );
    recorderRef.current = recorder;
    lastDataAtRef.current = Date.now();

    setPhase('recording');
    setElapsed(0);
    timerRef.current = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    watchdogRef.current = window.setInterval(() => {
      const since = Date.now() - lastDataAtRef.current;
      if (since <= WATCHDOG_MS) return;
      if (!watchdogTrippedRef.current) {
        watchdogTrippedRef.current = true;
        clientLog(
          'recorder.watchdog.trip',
          { msSinceLastData: since, state: recorder.state },
          { level: 'error', meetingId: id },
        );
      }
      setCaptureLost(true);
    }, RECORDING_TIMESLICE_MS / 2);
    void requestWakeLock();
  };

  /** Shared by Stop and by "retry finishing" after a failed stop. */
  const finish = async (): Promise<void> => {
    setError(null);
    setPhase('finishing');
    queueRef.current?.markStopping();
    clientLog(
      'recorder.stop.pressed',
      { chunks: chunkIndexRef.current, elapsedSec: elapsed, pending: queueState.pending },
      { level: 'info', meetingId: id },
    );
    if (timerRef.current) window.clearInterval(timerRef.current);
    clearWatchdog();

    try {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        await new Promise<void>((resolve) => {
          recorder.onstop = () => resolve();
          try {
            recorder.stop();
          } catch {
            resolve();
          }
        });
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      await closeAudioContext();
      setMicCheckState('idle');
      await releaseWakeLock();

      const queue = queueRef.current;
      if (queue) {
        await queue.flush();
        const result = await queue.complete(chunkIndexRef.current, elapsed);
        clientLog(
          'recorder.complete.ok',
          { totalChunks: chunkIndexRef.current, alreadyComplete: result.alreadyComplete ?? false },
          { level: 'info', meetingId: id },
        );
        await queue.discardPersisted();
      } else {
        await meetingsApi.completeRecording(id, chunkIndexRef.current, elapsed);
        clientLog(
          'recorder.complete.ok',
          { totalChunks: chunkIndexRef.current, viaQueue: false },
          { level: 'info', meetingId: id },
        );
      }

      setPhase('done');
      navigate(`/meetings/${id}`);
    } catch (err) {
      // Leave the take recoverable: the chunks are still on the server and in
      // IndexedDB, so the operator retries finishing rather than re-recording.
      clientLog(
        'recorder.complete.failed',
        {
          totalChunks: chunkIndexRef.current,
          errorName: err instanceof Error ? err.name : 'unknown',
          errorMessage: err instanceof Error ? err.message : String(err),
        },
        { level: 'error', meetingId: id },
      );
      setError(err instanceof Error ? err.message : t('common.error'));
      setPhase('stopFailed');
    }
  };

  const resume = async (): Promise<void> => {
    setError(null);
    const resumed = await ChunkQueue.resume(id, setQueueState);
    if (!resumed) {
      setResumable(null);
      return;
    }
    queueRef.current?.dispose();
    queueRef.current = resumed.queue;
    chunkIndexRef.current = resumed.chunks.length;
    setResumable(null);
    await finish();
  };

  const busy = phase === 'finishing';

  return (
    <>
      <h1>{meetingData?.meeting.title ?? t('recorder.title')}</h1>

      {error && <div className="banner error">{error}</div>}
      {!queueState.online && <div className="banner">{t('recorder.offline')}</div>}
      {queueState.stalled && <div className="banner error">{t('recorder.stalled')}</div>}
      {captureLost && phase === 'recording' && (
        <div className="banner error">{t('recorder.captureLost')}</div>
      )}
      {silenceWarning && phase === 'recording' && (
        <div className="banner error">{t('recorder.silenceWarning')}</div>
      )}
      {resumable !== null && phase === 'idle' && (
        <div className="banner">
          {t('recorder.resumeAvailable', { count: resumable })}{' '}
          <button type="button" className="link" onClick={() => void resume()}>
            {t('recorder.resume')}
          </button>
        </div>
      )}

      {phase === 'idle' && micCheckState === 'unavailable' && (
        <div className="banner error" role="alert">
          {t('recorder.micUnavailable')}
        </div>
      )}

      {phase === 'idle' && micCheckState !== 'unavailable' && (
        <div className="card mic-check">
          <div className="spread">
            <span className="muted">{t('recorder.micCheck')}</span>
            {micLabel !== null && (
              <span className="muted">
                {t('recorder.micLabel', { label: micLabel || t('recorder.micLabelUnknown') })}
              </span>
            )}
          </div>
          <div className="mic-meter" role="img" aria-label={t('recorder.micLevelAria')}>
            <div className="mic-meter-fill" style={{ width: `${Math.round(micLevel * 100)}%` }} />
          </div>
          {micCheckState === 'needsGesture' && (
            <button type="button" className="secondary" onClick={handleStartMicCheck}>
              {t('recorder.startMicCheck')}
            </button>
          )}
          {micDevices.length > 1 && micDevices.every((d) => d.label !== '') && (
            <div className="field mic-picker">
              <label htmlFor="mic-select">{t('recorder.selectMic')}</label>
              <select
                id="mic-select"
                value={selectedMicId ?? ''}
                onChange={(e) => void handleMicChange(e.target.value)}
              >
                {micDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ textAlign: 'center' }}>
        <div>
          {phase === 'recording' ? (
            <span className="chip live">
              <span className="pulse" />
              {t('recorder.live')}
            </span>
          ) : (
            <span className="chip">
              {t(
                `recorder.${phase === 'idle' ? 'idle' : phase === 'stopFailed' ? 'stopFailed' : 'finishing'}`,
              )}
            </span>
          )}
        </div>

        <div className="elapsed">{formatTimestamp(elapsed)}</div>

        {phase === 'idle' && (
          <button type="button" className="gold" onClick={() => void start(false)}>
            {t('recorder.start')}
          </button>
        )}
        {phase === 'recording' && (
          <button type="button" className="danger" onClick={() => void finish()}>
            {t('recorder.stop')}
          </button>
        )}
        {phase === 'finishing' && (
          <>
            <p className="muted">{t('recorder.finishing')}</p>
            {queueState.pending > 0 && (
              <p className="muted">
                {t('recorder.uploadingPiece', {
                  current: queueState.uploaded + 1,
                  total: queueState.uploaded + queueState.pending,
                })}
              </p>
            )}
            {queueState.lastError && (
              <p className="muted" style={{ color: 'var(--danger)' }}>
                {t('recorder.lastError')}: {queueState.lastError}
              </p>
            )}
          </>
        )}
        {/* Never offer Start here: a second take would be spliced onto this one. */}
        {phase === 'stopFailed' && (
          <>
            <p className="muted">{t('recorder.stopFailedHelp')}</p>
            <button type="button" className="gold" disabled={busy} onClick={() => void finish()}>
              {t('recorder.retryFinish')}
            </button>
          </>
        )}
        {phase === 'done' && <p className="muted">{t('recorder.done')}</p>}
      </div>

      <div className="card">
        <table>
          <tbody>
            <tr>
              <td>{t('recorder.uploaded')}</td>
              <td>{queueState.uploaded}</td>
            </tr>
            <tr>
              <td>{t('recorder.pending')}</td>
              <td>{queueState.pending}</td>
            </tr>
            {queueState.lastError && (
              <tr>
                <td style={{ color: 'var(--danger)' }}>
                  {queueState.stalled ? t('recorder.retrying') : t('recorder.lastError')}
                </td>
                <td style={{ color: 'var(--danger)' }}>{queueState.lastError}</td>
              </tr>
            )}
            {wakeLockHeld && (
              <tr>
                <td colSpan={2} className="muted">
                  {t('recorder.wakeLock')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="muted" style={{ marginBottom: 0 }}>
          {t('recorder.keepOpen')}
        </p>
      </div>
    </>
  );
}
